import { LoadingOverlay } from '@mantine/core';
import { useGetMergeableDebtors, useIgnoreDuplicates, useMergeSimilarDebtors } from 'api';
import type { MergeSimilarDebtorsBody } from 'api/models';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import HTML from 'shared/components/HTML/HTML';
import { Icon, IconName } from 'shared/components/Icon';
import { Button, ButtonColor } from 'shared/io';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';

import { AttributesSelection } from './AttributesSelection';
import styleIdentifiers from './DuplicatesClientDetail.module.scss';
import { MergeableDebtor } from './MergeableDebtor';

const styles = classNames.bind(styleIdentifiers);

export const DuplicatesClientDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { id: baseDebtorId } = useParams<{ id: string }>();

  const {
    data: mergeableDebtorsListResponse,
    isLoading,
    isError,
  } = useGetMergeableDebtors(baseDebtorId);

  const { mutate: mergeDebtors } = useMergeSimilarDebtors();
  const { mutate: ignoreDuplicates } = useIgnoreDuplicates();

  const [checkedDebtorId, setCheckedDebtorId] = useState<string>('');

  if (isLoading && !mergeableDebtorsListResponse) {
    return <LoadingOverlay visible />;
  }

  if (isError || !mergeableDebtorsListResponse) {
    return <span>Error</span>;
  }

  const debtorsToMerge = mergeableDebtorsListResponse.data || [];

  const goToList = () => {
    history.push('/clients/duplicates');
  };

  const skipMerge = () => {
    ignoreDuplicates(
      {
        id: baseDebtorId,
      },
      {
        onSuccess: () => {
          goToList();
        },
      },
    );
  };

  const submitMerge = (data: MergeSimilarDebtorsBody) => {
    mergeDebtors(
      {
        data,
      },
      {
        onSuccess: () => {
          dialogHide(DialogShowId.CUSTOM);
          goToList();
        },
      },
    );
  };

  const openAttributesSelectionModal = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      title: t(i18nKeys.CLIENT.MERGE.TITLE),
      children: (
        <AttributesSelection
          baseDebtorId={checkedDebtorId}
          debtors={debtorsToMerge}
          onSubmit={(data: MergeSimilarDebtorsBody) => submitMerge(data)}
        />
      ),
    });
  };

  return (
    <div className={styles('mergeable-clients')}>
      <div className={styles('title-line')}>
        <Icon name={IconName.MINIMAL_LEFT} onClick={goToList} /> <h1>{t(i18nKeys.CLIENTS)}</h1>
        <div className={styles('buttons-wrapper')}>
          <Button
            label={t(i18nKeys.CLIENT.MERGE.IGNORE)}
            onClick={skipMerge}
            color={ButtonColor.GREY}
            noMargin
          />
          <Button
            label={t(i18nKeys.MERGE_CLIENTS)}
            disabled={checkedDebtorId === ''}
            onClick={openAttributesSelectionModal}
            noMargin
          />
        </div>
      </div>
      <HTML className={styles('warning')} html={t(i18nKeys.CLIENT.MERGE.SELECT_CLIENT)} />
      <div className={styles('clients-wrapper')}>
        {debtorsToMerge.map(client => (
          <MergeableDebtor
            key={client.id}
            client={client}
            checkedDebtorId={checkedDebtorId}
            onCheck={(e: any) => {
              setCheckedDebtorId(e);
            }}
          />
        ))}
      </div>
    </div>
  );
};
